import classNames from 'classnames';
import React, { Ref } from 'react';
import Icon from '@material-ui/core/Icon';

export interface ButtonProps extends React.HTMLAttributes<HTMLElement> {
  className?: string;
  /**
   * ボタンラベル
   */
  label?: React.ReactNode;
  /**
   * ラベルのクラス名
   */
  labelClassName?: string;
  /**
   * 左側のボタンアイコン. MaterialIconsのアイコン名が入ります。
   * https://material.io/resources/icons
   */
  leftIcon?: string;
  /**
   * 左側のボタンアイコンの丸番号が入ります。
   */
  countNumber?: number;
  /**
   * 右側のボタンアイコン. MaterialIconsのアイコン名が入ります。
   * https://material.io/resources/icons
   */
  rightIcon?: string;
  /**
   * アイコンのクラス名
   */
  iconClassName?: string;
  /**
   * 無効
   */
  disabled?: boolean;
  /**
   * ディストラクティブ
   */
  isDanger?: boolean;
  /**
   * 角丸
   */
  isRound?: boolean;
  /**
   * ボタンのタイプ. デフォルトは `contained`
   */
  type?: 'contained' | 'outlined' | 'transparent' | 'secondary' | 'fab';
  /**
   * ボタンの横幅
   */
  width?: '3s' | '2s' | 's' | 'm' | 'ml' | 'l' | '2l' | 'full';
  /**
   * ボタンの高さ
   */
  height?: '2s' | 's' | 'm' | 'l' | '2l' | '3l';
  /**
   * `button` もしくは `input` タグを指定します。
   */
  as?: 'button' | 'input';

  /**
   * ref
   */
  customRef?: Ref<HTMLButtonElement>;
}

const Button: React.FC<ButtonProps> = ({
  label,
  leftIcon,
  countNumber,
  rightIcon,
  disabled = false,
  isDanger,
  isRound,
  type,
  width,
  height,
  className,
  iconClassName,
  labelClassName,
  as: Component = 'button',
  customRef,
  ...props
}) => {
  const classes = classNames(
    'uc-button',
    className,
    type && `uc-button--${type}`,
    width && `uc-button--w${width === 'full' ? '-' : ''}${width}`,
    height && `uc-button--h${height}`,
    disabled && 'uc-button--disabled',
    isDanger && 'uc-button--danger',
    isRound && 'uc-button--round',
  );

  if (Component === 'input' && typeof label === 'string')
    return <input type='submit' className={classes} disabled={disabled} value={label} {...props} />;

  /**
   * 左側のボタンアイコン表示
   *
   * countNumberがある場合、丸番号を返却する。
   * countNumberが無い場合、leftIconの形を返却する。
   *
   * @param countNumber 左側の丸番号
   * @param leftIcon    左側のボタンアイコン
   * @returns
   */
  const renderLeftIcon = (countNumber: number | undefined, leftIcon: string) => {
    if (countNumber) {
      return (
        <span
          className='uc-badge uc-badge--info u-text-middle u-mln8 u-mr8'
          style={{
            right: '20px',
          }}
          data-testid='search-branch-count'>
          {countNumber}
        </span>
      );
    } else if (leftIcon) {
      return <Icon className={classNames('uc-button__icon', iconClassName)}>{leftIcon}</Icon>;
    }
  };

  return (
    <button type='button' className={classes} disabled={disabled} ref={customRef} {...props}>
      {leftIcon && renderLeftIcon(countNumber, leftIcon)}
      {label && <span className={classNames('uc-button__label', labelClassName)}>{label}</span>}
      {rightIcon && <Icon className={classNames('uc-button__icon', iconClassName)}>{rightIcon}</Icon>}
    </button>
  );
};

export default Button;
